import { z } from 'zod'

// ─── FO-508 (spec 208) — telefoon en simkaart als set ────────────────────────
//
// Een medewerker krijgt telefoon + simkaart (= het abonnement met nummer en
// ICCID) altijd samen. Een losse koppeling (telefoon zonder simkaart, of
// simkaart zonder telefoon) kan alleen via een uitzonderingsaanvraag met reden,
// goedgekeurd door HR of directie (rol ADMIN). Eén gedeeld schema voor de API én
// het formulier (CLAUDE.md § Validatie).

export const MIDDEL_UITZONDERING_SOORTEN = ['TELEFOON_ZONDER_SIM', 'SIM_ZONDER_TELEFOON'] as const
export type MiddelUitzonderingSoort = (typeof MIDDEL_UITZONDERING_SOORTEN)[number]

export const MIDDEL_UITZONDERING_SOORT_LABELS: Record<MiddelUitzonderingSoort, string> = {
  TELEFOON_ZONDER_SIM: 'Telefoon zonder simkaart',
  SIM_ZONDER_TELEFOON: 'Simkaart zonder telefoon',
}

export const MIDDEL_UITZONDERING_REDENEN = [
  'TOESTEL_KAPOT',
  'TOESTEL_VERVANGEN',
  'SIM_VERVANGEN',
  'ABONNEMENT_GESTOPT',
  'EIGEN_TOESTEL',
  'OVERIG',
] as const
export type MiddelUitzonderingReden = (typeof MIDDEL_UITZONDERING_REDENEN)[number]

export const MIDDEL_UITZONDERING_REDEN_LABELS: Record<MiddelUitzonderingReden, string> = {
  TOESTEL_KAPOT: 'Telefoon kapot',
  TOESTEL_VERVANGEN: 'Telefoon wordt vervangen',
  SIM_VERVANGEN: 'Simkaart wordt vervangen',
  ABONNEMENT_GESTOPT: 'Abonnement gestopt of opgezegd',
  EIGEN_TOESTEL: 'Medewerker gebruikt een eigen telefoon',
  OVERIG: 'Anders (licht toe)',
}

export const MIDDEL_UITZONDERING_STATUSSEN = ['INGEDIEND', 'GOEDGEKEURD', 'AFGEWEZEN', 'INGETROKKEN', 'VERVALLEN'] as const
export type MiddelUitzonderingStatus = (typeof MIDDEL_UITZONDERING_STATUSSEN)[number]

export const MIDDEL_UITZONDERING_STATUS_LABELS: Record<MiddelUitzonderingStatus, string> = {
  INGEDIEND: 'Wacht op goedkeuring',
  GOEDGEKEURD: 'Goedgekeurd',
  AFGEWEZEN: 'Afgewezen',
  INGETROKKEN: 'Ingetrokken',
  VERVALLEN: 'Vervallen',
}

/** Open aanvragen ouder dan dit aantal dagen vervallen (dagelijkse tick). */
export const MIDDEL_UITZONDERING_VERVAL_DAGEN = 30

const ymd = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum als jjjj-mm-dd')

/** De geplande actie die pas bij goedkeuren wordt uitgevoerd. */
export const MiddelUitzonderingActieSchema = z.discriminatedUnion('type', [
  // TELEFOON_ZONDER_SIM: een telefoon uitgeven zonder nummer.
  z.object({
    type: z.literal('UITGEVEN'),
    uniekItemId: z.string().min(1),
    employeeId: z.string().min(1),
    datum: ymd.nullable().optional(),
    verwachteRetour: ymd.nullable().optional(),
  }),
  // SIM_ZONDER_TELEFOON: een nummer aan een medewerker hangen zonder telefoon.
  z.object({
    type: z.literal('KOPPELEN'),
    abonnementId: z.string().min(1),
    employeeId: z.string().min(1),
    datum: ymd.nullable().optional(),
  }),
  // Een BESTAANDE losse koppeling (oude data, imports) achteraf verantwoorden:
  // bij goedkeuren wordt alleen de reden vastgelegd, er verandert niets. Precies
  // één van `uniekItemId` (telefoon zonder simkaart) of `abonnementId` (simkaart
  // zonder telefoon).
  z.object({
    type: z.literal('VASTLEGGEN'),
    employeeId: z.string().min(1),
    uniekItemId: z.string().min(1).nullable().optional(),
    abonnementId: z.string().min(1).nullable().optional(),
  }),
])
export type MiddelUitzonderingActie = z.infer<typeof MiddelUitzonderingActieSchema>

export const CreateMiddelUitzonderingSchema = z
  .object({
    actie: MiddelUitzonderingActieSchema,
    redenCode: z.enum(MIDDEL_UITZONDERING_REDENEN, { message: 'Kies een reden' }),
    toelichting: z.string().trim().max(500, 'Toelichting is te lang (max. 500 tekens)').nullable().optional(),
  })
  .superRefine((d, ctx) => {
    if (d.actie.type === 'VASTLEGGEN' && !d.actie.uniekItemId === !d.actie.abonnementId) {
      ctx.addIssue({ code: 'custom', path: ['actie'], message: 'Kies een telefoon óf een nummer' })
    }
    if (d.redenCode === 'OVERIG' && !d.toelichting?.trim()) {
      ctx.addIssue({ code: 'custom', path: ['toelichting'], message: 'Licht de reden toe' })
    }
  })
export type CreateMiddelUitzonderingInput = z.infer<typeof CreateMiddelUitzonderingSchema>

export const BeoordeelMiddelUitzonderingSchema = z.object({
  notitie: z.string().trim().max(500, 'Notitie is te lang (max. 500 tekens)').nullable().optional(),
})
export const AfwijzenMiddelUitzonderingSchema = z.object({
  // `reden` (niet `notitie`): de goedkeuringen-inbox stuurt bij afwijzen { reden }.
  reden: z.string().trim().min(1, 'Geef aan waarom je afwijst').max(500, 'Reden is te lang (max. 500 tekens)'),
})

export const MiddelUitzonderingListQuerySchema = z.object({
  status: z.enum(MIDDEL_UITZONDERING_STATUSSEN).optional(),
  /** Alleen aanvragen voor deze telefoon of dit nummer (detailpagina's). */
  uniekItemId: z.string().min(1).optional(),
  abonnementId: z.string().min(1).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
})
export type MiddelUitzonderingListQuery = z.infer<typeof MiddelUitzonderingListQuerySchema>

export interface MiddelUitzonderingDTO {
  id: string
  soort: MiddelUitzonderingSoort
  status: MiddelUitzonderingStatus
  employee: { id: string; naam: string }
  /** Telefoon (toestel) waar het om gaat, of `null`. */
  toestel: { id: string; label: string } | null
  /** Nummer (abonnement) waar het om gaat, of `null`. */
  abonnement: { id: string; nummerWeergave: string | null; naam: string } | null
  redenCode: MiddelUitzonderingReden
  toelichting: string | null
  aangevraagdDoor: { id: string; naam: string | null }
  aangevraagdOp: string
  beoordeeldDoor: { id: string; naam: string | null } | null
  beoordeeldOp: string | null
  beoordelingNotitie: string | null
  uitgevoerdOp: string | null
  vervallenReden: string | null
  /** Mag de huidige gebruiker deze aanvraag beoordelen (rol + vier-ogen + status)? */
  magBeoordelen: boolean
  /** Mag de huidige gebruiker deze aanvraag intrekken (eigen + open)? */
  magIntrekken: boolean
}

// ─── Set-regel: foutdetails + losse koppelingen ──────────────────────────────

/** Details bij 422 `SET_ONVOLLEDIG`: wat ontbreekt en wat er wél kan. */
export interface SetOnvolledigDetails {
  soort: MiddelUitzonderingSoort
  /** Telefoons van de medewerker zonder lopend nummer (bij SIM_ZONDER_TELEFOON). */
  toestellen: { id: string; label: string }[]
  /** Aantal nummers in voorraad om aan een telefoon te koppelen (bij TELEFOON_ZONDER_SIM). */
  nummersInVoorraad: number
  /** Open aanvraag voor dit toestel/nummer, als die er al is. */
  openAanvraagId: string | null
}

export interface LosseKoppelingDTO {
  soort: MiddelUitzonderingSoort
  employee: { id: string; naam: string }
  toestel: { id: string; label: string } | null
  abonnement: { id: string; nummerWeergave: string | null; naam: string } | null
  /** Goedgekeurde uitzondering die deze koppeling verklaart, of `null`. */
  uitzondering: { id: string; redenCode: MiddelUitzonderingReden; toelichting: string | null } | null
  /** Open aanvraag voor deze koppeling, of `null`. */
  openAanvraagId: string | null
}

export const LosseKoppelingenQuerySchema = z.object({
  soort: z.enum(MIDDEL_UITZONDERING_SOORTEN),
  /** Alleen koppelingen zonder goedgekeurde uitzondering. */
  zonderUitzondering: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => v === 'true'),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
})
export type LosseKoppelingenQuery = z.infer<typeof LosseKoppelingenQuerySchema>
